import { useContext, useEffect, useState } from 'react';

import InventoryDetailContext from '../InventoryDetailContext';
import useAssetUsage from '../useAssetUsage';

import ConversionUnitSelector from './ConversionUnitSelector';
import CostHeader from './CostHeader';
import {
  getActiveConversionUnits,
  getDefaultConversionUnit,
} from './costTabUtilities';
import UsageTable, { RESULTS_PER_PAGE_OPTIONS } from './UsageTable';

import classes from './costTab.module.css';

const CostTab = () => {
  const {
    asset,
    conversionUnits: allConversionUnits,
    conversionUnitSelection,
    setConversionUnitSelection,
  } = useContext(InventoryDetailContext);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(RESULTS_PER_PAGE_OPTIONS[0]);

  const unit = asset?.budget?.unit;
  const conversionUnits = getActiveConversionUnits(allConversionUnits);

  // held by id, since two units can share a name with different conversions.
  const selectedConversionUnit =
    conversionUnits.find(
      conversionUnit =>
        conversionUnitSelection?.assetId === asset.id &&
        conversionUnit.id === conversionUnitSelection.conversionUnitId,
    ) ?? getDefaultConversionUnit(conversionUnits);

  const onConversionUnitChange = (conversionUnitId: string) =>
    setConversionUnitSelection({ assetId: asset.id, conversionUnitId });

  const onLimitChange = (nextLimit: number) => {
    setLimit(nextLimit);
    setPage(1);
  };

  const {
    summary,
    summaryLoading,
    summaryCalled,
    fetchSummary,
    usage,
    totalCount,
    usageLoading,
    usageCalled,
    fetchUsage,
  } = useAssetUsage({ assetId: asset.id });

  const quantityOnHand = asset?.budget?.quantityOnHand;

  useEffect(() => {
    if (asset.isMaterial) {
      fetchUsage({ page, limit });
    }
  }, [asset.id, page, limit, quantityOnHand]);

  useEffect(() => {
    if (asset.isMaterial) {
      fetchSummary();
    }
  }, [asset.id, quantityOnHand]);

  return (
    <div className={classes.tabContent}>
      <div className={classes.leftBodyPaneFixed} />
      <div className={classes.rightBodyPaneFill}>
        {conversionUnits.length > 0 && (
          <ConversionUnitSelector
            baseUnit={unit}
            conversionUnits={conversionUnits}
            selectedConversionUnit={selectedConversionUnit}
            onChange={onConversionUnitChange}
          />
        )}
        <CostHeader
          summary={summary}
          summaryLoading={
            asset.isMaterial && (summaryLoading || !summaryCalled)
          }
          conversionUnit={selectedConversionUnit}
        />
        <div className={classes.contentContainer}>
          <UsageTable
            usage={usage}
            totalCount={totalCount}
            usageLoading={asset.isMaterial && (usageLoading || !usageCalled)}
            page={page}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={onLimitChange}
            unit={unit}
            conversionUnit={selectedConversionUnit}
          />
        </div>
      </div>
    </div>
  );
};

export default CostTab;
